import { type RootState, store } from "@store";
import type { EntityType, StoryType } from "@/config/types";
import { track } from "@/features/analytics";
import type { IVersion } from "@/types";

const WIZARD_KEYS: string[] = [
  "company product",
  "website",
  "company name",
  "subtype category",
  "template title",
  "template id",
  "website flow",
  "wizard skipped",
  "branded template",
  "template structure version",
  "wizard doc subtype",
  "wizard doc category",
  "wizard environment",
];

const getStoryState = (state: RootState) => state.story;

const editCounts = new Map<string, number>();

const getNextMilestone = (currentCount: number): number => {
  const milestones = [1, 2, 5, 10, 25, 50, 100, 250, 500, 1000];
  const nextMilestone = milestones.find((m) => currentCount < m);
  if (nextMilestone) return nextMilestone;

  // For larger counts, use exponential scaling
  const base = 2.5;
  const scale = 5;
  const power = Math.ceil(Math.log(currentCount / scale) / Math.log(base));
  return Math.round(scale * base ** power);
};

const shouldTrackEdit = (storyId: string): boolean => {
  const currentCount = editCounts.get(storyId) || 0;
  const newCount = currentCount + 1;
  editCounts.set(storyId, newCount);

  return newCount === getNextMilestone(currentCount);
};

const extractAIClassifications = (htmlString: string): string[] => {
  const regex = /data-ai-classification="([^"]+)"/g;
  return Array.from(htmlString.matchAll(regex), (match) => match[1]);
};

const trackContent = (event: string, props: Record<string, unknown> = {}) => {
  const { story, version } = getStoryState(store.getState());

  if (!story) return;

  if (version) {
    trackVersion(event, story, version, props);
  } else {
    trackStory(event, story, props);
  }
};

const trackEntityAction = ({
  entity,
  action,
  flatContentLayers,
  id,
}: {
  entity: EntityType;
  action: "edit" | "delete";
  flatContentLayers: Record<string, EntityType>;
  id?: string;
}) => {
  if (!entity || !id) return;

  // Only track edits at specific milestones
  if (action === "edit" && !shouldTrackEdit(id)) {
    return;
  }

  const slideEntity =
    entity.type === "slide"
      ? entity
      : getSelectedParent("slide", entity, flatContentLayers);

  const slideName = slideEntity?.settings?.internalSlideName || null;

  const trackClassification = (classification?: string) => {
    track(`${action} entity`, {
      "entity type": entity.type,
      "entity name": entity.settings.name,
      ...(slideName && {
        "slide name": slideName,
      }),
      ...(classification && {
        "ai classification name": classification,
      }),
      ...(action === "edit" && {
        edits: editCounts.get(id),
      }),
    });
  };

  if (entity.type !== "element") {
    trackClassification();
    return;
  }

  const { AIClassificationAnchor, content } = entity.settings;

  if (AIClassificationAnchor && typeof AIClassificationAnchor === "string") {
    trackClassification(AIClassificationAnchor);
    return;
  }

  if (content && typeof content === "string") {
    try {
      const classifications = extractAIClassifications(content);
      classifications.forEach(trackClassification);
      return;
    } catch {
      track("failed extracting classifications", {
        "entity type": entity.type,
        "entity name": entity.settings.name,
      });
    }
  }

  trackClassification();
};

const trackStory = (
  event: string,
  story: StoryType,
  payload: Record<string, unknown> = {},
) => {
  if (!story) return track(event, payload);
  const wizard: Record<string, unknown> = {};
  const wizardPayload = story.settings?.wizard ? story.settings.wizard : null;
  if (wizardPayload && typeof wizardPayload === "object") {
    for (const k of WIZARD_KEYS) {
      if (k in wizardPayload) wizard[k] = wizardPayload[k];
    }
  }
  track(event, {
    "story name": story.settings.title,
    "story created": story.createdAt,
    "story status": story.status,
    "editor version": "5",
    ...payload,
    ...wizard,
  });
};

const trackVersion = (
  event: string,
  story: StoryType,
  version: IVersion,
  payload: Record<string, unknown> = {},
) => {
  if (!version) return trackStory(event, story, payload);
  const expireAt = version.isPublic ? null : version.expireAt;
  trackStory(event, story, {
    "story version name": version.data ? version.data.title : "Unknown",
    "story version created": version.createdAt,
    "story version expire": expireAt,
    ...payload,
  });
};

const getSelectedParent = (
  targetType: EntityType["type"],
  entity: EntityType,
  flatContentLayers: Record<string, EntityType>,
): EntityType | null => {
  let currentEntity = entity;

  while (currentEntity) {
    if (currentEntity.type === targetType) {
      return currentEntity;
    }
    if (!currentEntity.parentId) break;
    currentEntity = flatContentLayers[currentEntity.parentId];
  }

  return null;
};

export { trackContent, trackEntityAction };
